import { useTranslation } from 'react-i18next';
import { useFormContext, useFormState } from 'react-hook-form';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import type { ReactNode } from 'react';
import type {
  AgreementsNames,
  LiberisAgreementsNames,
  PrefixedAgreementsNames,
} from '@repo/shared/types';
import { marketingAgreementType } from '@repo/shared/types';
import { Checkbox } from '@repo/ui';
import type { MerchantResponse } from '@repo/api/endpoints/merchant';
import { useIsMobile } from '@repo/mui-config/hooks';

import type { ModalStateUnion } from '@/components/modal/modal.store';
import { getCurrentAgreements } from '@/components/loan-form/loan-agreements/agreements-helper';

import { AgreementsWrapper } from './agreements-wrapper';

interface AgreementsProps {
  title: string;
  link?: string;
  modalBodyChildren?: ReactNode;
  name: AgreementsNames | PrefixedAgreementsNames | LiberisAgreementsNames;
  type?: ModalStateUnion;
}

interface Agreements {
  merchantResponseData: MerchantResponse;
  agreements: AgreementsProps[];
  isLiberisAgreements?: boolean;
  isPragmaMultiprovider?: boolean;
  selectAllName?: string;
  includeP24MarketingAgreements?: boolean;
}

export function Agreements({
  merchantResponseData,
  agreements,
  isLiberisAgreements,
  isPragmaMultiprovider,
  selectAllName,
  includeP24MarketingAgreements,
}: Agreements) {
  const { t } = useTranslation();
  const context = useFormContext();
  const isMobile = useIsMobile();
  context.watch();
  const { errors } = useFormState({ control: context.control });

  const currentAgreementFieldNames = getCurrentAgreements({
    data: merchantResponseData.data,
    isLiberisAgreements,
    isPragmaMultiprovider,
  });

  // When the P24 marketing form (email/sms/tel) is rendered alongside, include its
  // fields so "select all" also toggles them and reflects their checked state.
  const selectAllFieldNames = includeP24MarketingAgreements
    ? [...currentAgreementFieldNames, ...marketingAgreementType.options]
    : currentAgreementFieldNames;

  const areAllAgreementsChecked = !selectAllFieldNames
    .map((fieldName) => context.getValues(fieldName))
    .includes(false);

  const onToggleAllAgreements = () => {
    selectAllFieldNames.forEach((fieldName) => {
      context.setValue(fieldName, !areAllAgreementsChecked);
    });
    void context.trigger();
  };

  const isRequiredAgreementsError = currentAgreementFieldNames
    .filter((agreementName) => agreementName !== 'marketing')
    .find((agreementName) => Boolean(context.getFieldState(agreementName).error));

  return (
    <Box>
      {errors && isRequiredAgreementsError ? (
        <FormHelperText data-testid="field-agreements-error" error sx={{ marginLeft: 0 }}>
          {t('components.agreements.validation')}
        </FormHelperText>
      ) : null}

      <Grid
        container
        gap={{ lg: '8px', xl: '0px' }}
        sx={{
          marginLeft: isMobile ? '10px' : '16px',
        }}
      >
        <Grid>
          <AgreementsWrapper title="components.agreements.checkAll">
            <Checkbox
              checked={areAllAgreementsChecked}
              name={selectAllName || 'selectAllCheckbox'}
              noSpacing
              onClick={onToggleAllAgreements}
              sx={{
                marginRight: '10px',
              }}
            />
          </AgreementsWrapper>
        </Grid>
        {agreements
          .filter((agreement) => {
            return currentAgreementFieldNames.includes(agreement.name);
          })
          .map((agreement) => (
            <Grid
              key={agreement.name}
              sx={{
                marginTop: '8px',
              }}
              size={12}
            >
              <AgreementsWrapper
                link={agreement.link}
                modalBodyChildren={agreement.modalBodyChildren}
                title={agreement.title}
                type={agreement.type}
              >
                <Checkbox
                  data-testid={`field-${agreement.name}`}
                  name={agreement.name}
                  noSpacing
                  sx={{
                    paddingTop: '0px',
                    marginRight: '10px',
                  }}
                />
              </AgreementsWrapper>
            </Grid>
          ))}
      </Grid>
    </Box>
  );
}
